import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Paper,
  Stack,
  alpha,
  Divider,
  Grid,
  CircularProgress,
  Chip,
  Button,
} from '@mui/material';
import {
  InfoOutlined,
  EditOutlined,
  AdminPanelSettingsOutlined,
  DescriptionOutlined,
  SecurityOutlined,
} from '@mui/icons-material';
import { getUserRoles } from '../../../helpers/api';
import { loadState } from '../../../store/localStorage';
import { openNotification } from '../../../helpers/notification';
import EditRole from './EditRole/EditRole';

const RoleList = () => {
  const [accountId, setAccountId] = useState('');
  const [roles, setRoles] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editRoleModal, setEditRoleModal] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState('');

  const [selectedRoleName, setSelectedRoleName] = useState('');
  const [selectedRoleDescription, setSelectedRoleDescription] = useState('');
  const [selectedRolePrivileges, setSelectedRolePrivileges] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');

  useEffect(() => {
    const data = loadState();
    const id = data?.auth?.result?.data?.accountId;
    if (id) setAccountId(id);
  }, []);

  useEffect(() => {
    if (accountId) refreshRoleList();
  }, [accountId]);

  const refreshRoleList = () => {
    setLoading(true);
    getUserRoles(accountId)
      .then((res) => {
        if (res.result.status.code === '00') {
          setRoles(res.result.data);
        }
      })
      .catch((error) => {
        openNotification({
          type: 'error',
          title: 'Error',
          message: `Could not get roles: ${error}`,
        });
      })
      .finally(() => setLoading(false));
  };

  const handleOpenEdit = (role) => {
    setSelectedRoleId(role.roleId);
    setSelectedRoleName(role.roleName);
    setSelectedRoleDescription(role.roleDescription);
    setSelectedRolePrivileges(role.privileges);
    setSelectedCategory(role.userCategory);
    setEditRoleModal(true);
  };

  const closeEditModal = () => setEditRoleModal(false);

  // TOOLTIP CONTENT GENERATOR
  const renderPrivilegesTooltip = (privileges) => (
    <Box sx={{ p: 1, maxWidth: 300 }}>
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 700, mb: 1, borderBottom: '1px solid rgba(255,255,255,0.2)', pb: 0.5 }}
      >
        Role Privileges
      </Typography>
      {privileges && privileges.length > 0 ? (
        <Stack spacing={1.5}>
          {privileges.map((p, idx) => (
            <Box key={idx}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  color: alpha('#fff', 0.7),
                }}
              >
                {p.subDivision}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.2 }}>
                {p.description}
              </Typography>
            </Box>
          ))}
        </Stack>
      ) : (
        <Typography variant="caption">No permissions assigned.</Typography>
      )}
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {editRoleModal && (
        <EditRole
          open={editRoleModal}
          close={closeEditModal}
          roleId={selectedRoleId}
          roleName={selectedRoleName}
          desc={selectedRoleDescription}
          privileges={selectedRolePrivileges}
          category={selectedCategory}
        />
      )}

      <Stack spacing={2}>
        {roles && roles.length > 0 ? (
          roles.map((role) => (
            <Paper
              key={role.roleId}
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: '16px',
                border: '1px solid',
                borderColor: 'divider',
                transition: '0.3s',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: `0 4px 20px ${alpha('#000', 0.05)}`,
                  bgcolor: alpha('#f5f7ff', 0.5),
                },
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                {/* ROLE NAME & CATEGORY */}
                <Grid item xs={12} md={4}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: '10px',
                        bgcolor: alpha('#0c5bab', 0.1),
                        color: 'primary.main',
                        display: 'flex',
                      }}
                    >
                      <AdminPanelSettingsOutlined />
                    </Box>
                    <Box>
                      <Typography variant="body1" fontWeight={700} color="text.primary">
                        {role.roleName}
                      </Typography>
                      <Chip
                        label={role.userCategory}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          mt: 0.5,
                          bgcolor: 'divider',
                        }}
                      />
                    </Box>
                  </Stack>
                </Grid>

                {/* DESCRIPTION */}
                <Grid item xs={12} md={5}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <DescriptionOutlined sx={{ fontSize: 18, color: 'text.disabled', mt: 0.3 }} />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontStyle: role.roleDescription ? 'normal' : 'italic' }}
                    >
                      {role.roleDescription || 'No description provided.'}
                    </Typography>
                  </Stack>
                </Grid>

                {/* ACTIONS */}
                <Grid item xs={12} md={3}>
                  <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                    <Tooltip
                      title={renderPrivilegesTooltip(role.privileges)}
                      arrow
                      placement="left"
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: '#1a2027',
                            borderRadius: '12px',
                            p: 1.5,
                            boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                          },
                        },
                      }}
                    >
                      <IconButton
                        size="small"
                        sx={{
                          bgcolor: alpha('#0c5bab', 0.05),
                          color: 'primary.main',
                          '&:hover': { bgcolor: 'primary.main', color: '#fff' },
                        }}
                      >
                        <SecurityOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ mx: 0.5, height: 20, alignSelf: 'center' }}
                    />

                    <Button
                      startIcon={<EditOutlined fontSize="small" />}
                      onClick={() => handleOpenEdit(role)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: '8px',
                        color: 'text.secondary',
                        '&:hover': { color: 'primary.main', bgcolor: alpha('#0c5bab', 0.05) },
                      }}
                    >
                      Edit Role
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          ))
        ) : (
          <Box
            sx={{
              py: 10,
              textAlign: 'center',
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: '20px',
            }}
          >
            <Typography variant="h6" color="text.disabled" fontWeight={700}>
              No Roles Created Yet
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default RoleList;
